import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { InstantBuy } from '@sorare/core/src/atoms/icons/InstantBuy';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMatchdayPicksTab } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import background from './assets/background.webp';
import picks from './assets/picks.webp';
import picksMobile from './assets/picks_mobile.webp';

const root = css`
  display: block;
  position: relative;
  width: 100%;
  min-height: calc(31 * var(--unit));
  padding: var(--double-unit);
  overflow: hidden;
  /* A transparent border reveals the border-box layer, which is how CSS fakes a
     gradient stroke. */
  border: 1px solid transparent;
  border-radius: var(--unit);
  /* The asset is a wide, short pattern, so covering a tall mobile banner blows it
     up: tile it at the scale the desktop banner renders it at instead. */
  --banner-background-layout: top center / auto calc(20 * var(--unit)) repeat;
  background:
    url(${background}) var(--banner-background-layout) padding-box,
    linear-gradient(0deg, var(--c-modes-pro-tertiary) 0%, var(--c-nd-150) 45%)
      border-box;

  @media ${tabletAndAbove} {
    --banner-background-layout: bottom center / cover no-repeat;

    min-height: calc(14 * var(--unit));
  }
`;

const artwork = css`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const desktopArtwork = css`
  display: none;
  height: calc(15 * var(--unit));

  @media ${tabletAndAbove} {
    display: block;
  }
`;

const mobileArtwork = css`
  height: 80%;

  @media ${tabletAndAbove} {
    display: none;
  }
`;

/* Sits between the cards and the wording so the text stays legible over them. */
const scrim = css`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--c-rgb-black), 0) 0%,
    var(--c-black) 100%
  );
  height: 50%;
  top: 50%;

  @media ${tabletAndAbove} {
    display: none;
  }
`;

/* Mobile stacks the wording under the cards, so the row only applies from tablet. */
const layout = css`
  position: relative;
  align-items: flex-end;
  min-height: inherit;

  @media ${tabletAndAbove} {
    align-items: center;
  }
`;

const cta = css`
  position: absolute;
  top: 0;
  right: 0;

  @media ${tabletAndAbove} {
    position: static;
  }
`;

const callToAction = css`
  display: none;

  @media ${tabletAndAbove} {
    display: flex;
  }
`;

export const FlashSalesBanner = ({ sport }: { sport: Sport }) => {
  const { generateSportContextPath } = useSportContext();
  const { show } = useMatchdayPicksTab({ sport });

  if (!show) {
    return null;
  }

  return (
    <ButtonBase
      className={root}
      to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS)}
    >
      <img
        className={classNames(artwork, mobileArtwork)}
        src={picksMobile}
        alt=""
      />
      <img className={classNames(artwork, desktopArtwork)} src={picks} alt="" />
      <div className={scrim} />
      <SBHorizontal gap={2} className={layout}>
        <Vertical gap={0.5} className="text-left">
          <Horizontal gap={0.5}>
            <FontAwesomeIcon
              icon={iconLightningBolt}
              color="var(--c-modes-pro-tertiary)"
            />
            <BrandM color="var(--c-modes-pro-tertiary)">
              <FormattedMessage defaultMessage="Flash sales" />
            </BrandM>
          </Horizontal>
          <BrandM>
            <FormattedMessage defaultMessage="Locked prices" />
          </BrandM>
        </Vertical>

        <Horizontal gap={2} center className={cta}>
          <Horizontal gap={1} center className={callToAction}>
            <InstantBuy size={2} color="var(--c-modes-pro-tertiary)" />
            <BrandS className="text-left">
              <FormattedMessage defaultMessage="While supplies last" />
            </BrandS>
          </Horizontal>
          <Button
            asSpan
            size="medium"
            color="tertiary"
            startIcon={<FontAwesomeIcon icon={iconChevronRight} />}
          />
        </Horizontal>
      </SBHorizontal>
    </ButtonBase>
  );
};
